import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Pencil, Plus, Trash2, X } from "lucide-react";
import {
  CAMPAIGN_STATUSES,
  CAMPAIGN_STATUS_STYLES,
  CAMPAIGN_TYPES,
  CAMPAIGN_TYPE_STYLES,
  MONTH_THEMES,
  type CampaignStatus,
  type CampaignType,
  calendarTotals,
  dayString,
  monthGrid,
  overlapsRange,
  monthRange,
  statusStyle,
  typeStyle,
} from "@shared/marketingCalendar";

/**
 * The marketing and social posting calendar, nested into the portal.
 *
 * This is the calendar Altitude has been using as a standalone HTML file,
 * rebuilt as a component over real storage. What is deliberately the same: the
 * campaign types and their colours, the status ladder, the seasonal month
 * headers, the month grid with chips and the editable campaign list. Staff and
 * clients already read those colours, and a redesign during the move would
 * make a familiar tool feel like a new one.
 *
 * What is deliberately different: the standalone version persisted by writing
 * its own state back into a copy of its HTML, which means the second person to
 * save a month silently discards the first. Here every change is a mutation
 * against one row, so Altitude and the client are editing the same calendar.
 *
 * WHY THE MUTATIONS ARE PROPS
 * ---------------------------
 * The same calendar renders on the client dashboard, where the engagement
 * comes from the session cookie, and on the staff desk, where it comes from
 * the row being worked on. Those are two different tRPC procedures with two
 * different inputs. Passing the three callbacks in means one component serves
 * both without a `isStaff` flag deciding which API to call - the kind of flag
 * that eventually calls the wrong one.
 */

export interface CalendarCampaign {
  id: number;
  name: string;
  type: string;
  status: string;
  channel?: string | null;
  startsOn: string;
  endsOn?: string | null;
  owner?: string | null;
  notes?: string | null;
  postCopy?: string | null;
  lastEditedBy?: string | null;
}

export interface CampaignDraft {
  name: string;
  type: CampaignType;
  status: CampaignStatus;
  channel?: string | null;
  startsOn: string;
  endsOn?: string | null;
  owner?: string | null;
  notes?: string | null;
  postCopy?: string | null;
}

interface Props {
  campaigns: CalendarCampaign[];
  /** False for the roles that may read the calendar but not change it. */
  editable: boolean;
  onCreate?: (draft: CampaignDraft) => void;
  onUpdate?: (id: number, patch: Partial<CampaignDraft>) => void;
  onDelete?: (id: number) => void;
  busy?: boolean;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const prettyDay = (day: string) => {
  // Parsed as UTC parts rather than `new Date(day)`, which in some engines
  // reads a bare "2026-07-04" as UTC midnight and then prints it in local time
  // as the third of July.
  const [year, month, date] = day.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, date)).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

export function MarketingCalendar({ campaigns, editable, onCreate, onUpdate, onDelete, busy }: Props) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [monthIndex, setMonthIndex] = useState(today.getMonth());
  const [editing, setEditing] = useState<number | "new" | null>(null);

  const theme = MONTH_THEMES[monthIndex];
  const { first, last } = monthRange(year, monthIndex);

  // Filtered to the month in view here rather than refetched per month: one
  // engagement's calendar is dozens of rows, and paging the month selector
  // through the network makes a control that should feel instant feel slow.
  const inMonth = useMemo(
    () => campaigns.filter(campaign => overlapsRange(campaign, first, last)),
    [campaigns, first, last]
  );
  const weeks = useMemo(() => monthGrid(year, monthIndex, inMonth), [year, monthIndex, inMonth]);
  const totals = useMemo(() => calendarTotals(inMonth), [inMonth]);
  const todayIso = dayString(today.getFullYear(), today.getMonth(), today.getDate());

  const step = (delta: number) => {
    const next = monthIndex + delta;
    if (next < 0) { setMonthIndex(11); setYear(year - 1); return; }
    if (next > 11) { setMonthIndex(0); setYear(year + 1); return; }
    setMonthIndex(next);
  };

  const editingCampaign = typeof editing === "number" ? inMonth.find(c => c.id === editing) ?? campaigns.find(c => c.id === editing) : undefined;

  return (
    <div className="mkt">
      <header
        className="mkt__banner"
        style={{ background: `linear-gradient(135deg,${theme.gradient[0]},${theme.gradient[1]})` }}
      >
        <button type="button" aria-label="Previous month" onClick={() => step(-1)}><ChevronLeft size={18} /></button>
        <div>
          <strong>{theme.month} {year}</strong>
          <span>{theme.tag}</span>
        </div>
        <button type="button" aria-label="Next month" onClick={() => step(1)}><ChevronRight size={18} /></button>
      </header>

      <div className="mkt__stats">
        <article><strong>{totals.total}</strong><span>Campaigns</span></article>
        <article><strong>{totals.live}</strong><span>Live now</span></article>
        <article><strong>{totals.awaiting}</strong><span>Not started</span></article>
        <article className="mkt__stats-bar">
          <span>Month completion</span>
          <div className="mkt__bar"><i style={{ width: `${totals.completionPercent}%` }} /></div>
          <small>{totals.completionPercent}%</small>
        </article>
      </div>

      {/* Wrapper exists so the grid can scroll sideways on a phone instead of
          crushing seven columns into 340px. */}
      <div className="mkt__grid-scroll">
      <table className="mkt__grid">
        <thead>
          <tr>{WEEKDAYS.map(day => <th key={day} scope="col">{day}</th>)}</tr>
        </thead>
        <tbody>
          {weeks.map((week, index) => (
            <tr key={index}>
              {week.map((cell, cellIndex) => (
                <td
                  key={cell.day ?? `pad-${cellIndex}`}
                  className={cell.day ? undefined : "is-pad"}
                  data-today={cell.day === todayIso || undefined}
                >
                  {cell.dayOfMonth && <span className="mkt__daynum">{cell.dayOfMonth}</span>}
                  {cell.campaigns.map(campaign => {
                    const style = typeStyle(campaign.type);
                    return (
                      <button
                        key={campaign.id}
                        type="button"
                        className="mkt__chip"
                        style={{ background: style.color }}
                        title={`${campaign.name} — ${style.label} — ${statusStyle(campaign.status).label}`}
                        onClick={() => setEditing(campaign.id)}
                      >
                        {style.icon} {campaign.name}
                      </button>
                    );
                  })}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {totals.byType.length > 0 && (
        <ul className="mkt__legend">
          {totals.byType.map(entry => (
            <li key={entry.type}>
              <i style={{ background: entry.color }} />{entry.label} · {entry.count}
            </li>
          ))}
        </ul>
      )}

      <div className="mkt__list-head">
        <h3>{theme.month} campaigns</h3>
        {editable && (
          <button type="button" className="button button--coral" onClick={() => setEditing("new")}>
            <Plus size={14} />Add campaign
          </button>
        )}
      </div>

      {inMonth.length === 0 ? (
        <p className="cp-empty">Nothing scheduled for {theme.month}.</p>
      ) : (
        <ul className="mkt__list">
          {inMonth.map(campaign => {
            const style = typeStyle(campaign.type);
            const status = statusStyle(campaign.status);
            return (
              <li key={campaign.id}>
                <i style={{ background: style.color }} aria-hidden="true" />
                <div className="mkt__list-main">
                  <strong>{campaign.name}</strong>
                  <small>
                    {prettyDay(campaign.startsOn)}
                    {campaign.endsOn && campaign.endsOn !== campaign.startsOn ? ` – ${prettyDay(campaign.endsOn)}` : ""}
                    {campaign.channel ? ` · ${campaign.channel}` : ""}
                    {campaign.owner ? ` · ${campaign.owner}` : ""}
                  </small>
                </div>
                {editable && onUpdate ? (
                  <label className="mkt__status">
                    <span className="sr-only">Status for {campaign.name}</span>
                    <select
                      value={campaign.status}
                      disabled={busy}
                      style={{ background: status.color }}
                      onChange={event => onUpdate(campaign.id, { status: event.target.value as CampaignStatus })}
                    >
                      {CAMPAIGN_STATUSES.map(value => (
                        <option key={value} value={value}>{statusStyle(value).label}</option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <em className="mkt__status-flat" style={{ background: status.color }}>{status.label}</em>
                )}
                {editable && (
                  <div className="mkt__row-actions">
                    <button type="button" aria-label={`Edit ${campaign.name}`} onClick={() => setEditing(campaign.id)}>
                      <Pencil size={14} />
                    </button>
                    {onDelete && (
                      <button
                        type="button"
                        aria-label={`Delete ${campaign.name}`}
                        onClick={() => {
                          // Confirmed because there is no undo on a deleted
                          // row, and a mis-click on a dense list is easy.
                          if (window.confirm(`Delete "${campaign.name}" from the calendar?`)) onDelete(campaign.id);
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {editable && editing !== null && (
        <CampaignEditor
          campaign={editing === "new" ? undefined : editingCampaign}
          defaultDay={dayString(year, monthIndex, 1)}
          busy={busy}
          onClose={() => setEditing(null)}
          onSubmit={draft => {
            if (editing === "new") onCreate?.(draft);
            else if (typeof editing === "number") onUpdate?.(editing, draft);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Add or edit one campaign.
 *
 * Rendered inline rather than in a portal-to-body modal. The one on the
 * Blueprint page had to be portalled because a transformed ancestor was
 * capturing its fixed positioning; this sits inside ordinary page flow and
 * needs no such escape, so it stays where it is in the DOM and keeps its
 * focus order.
 */
function CampaignEditor({
  campaign,
  defaultDay,
  busy,
  onClose,
  onSubmit,
}: {
  campaign?: CalendarCampaign;
  defaultDay: string;
  busy?: boolean;
  onClose: () => void;
  onSubmit: (draft: CampaignDraft) => void;
}) {
  const [error, setError] = useState<string | null>(null);

  return (
    <form
      className="mkt__editor"
      onSubmit={event => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        const startsOn = String(data.get("startsOn") ?? "");
        const endsOn = String(data.get("endsOn") ?? "").trim();

        // Checked here as well as on the server. The server's answer is the one
        // that counts; this one spares the round trip and keeps the typing in
        // the form while it is corrected.
        if (endsOn && endsOn < startsOn) {
          setError("The end date cannot be before the start date.");
          return;
        }
        setError(null);

        onSubmit({
          name: String(data.get("name") ?? "").trim(),
          type: String(data.get("type")) as CampaignType,
          status: String(data.get("status")) as CampaignStatus,
          channel: String(data.get("channel") ?? "").trim() || null,
          startsOn,
          endsOn: endsOn || null,
          owner: String(data.get("owner") ?? "").trim() || null,
          notes: String(data.get("notes") ?? "").trim() || null,
          postCopy: String(data.get("postCopy") ?? "").trim() || null,
        });
      }}
    >
      <header>
        <h4>{campaign ? "Edit campaign" : "New campaign"}</h4>
        <button type="button" aria-label="Close" onClick={onClose}><X size={16} /></button>
      </header>

      <div className="mkt__editor-grid">
        <label className="field mkt__editor-wide">
          <span>Campaign</span>
          <input name="name" required maxLength={200} defaultValue={campaign?.name ?? ""} autoFocus placeholder="e.g. New Year Challenge launch" />
        </label>
        <label className="field">
          <span>Type</span>
          <select name="type" defaultValue={campaign?.type ?? "social"}>
            {CAMPAIGN_TYPES.map(value => (
              <option key={value} value={value}>{typeStyle(value).label}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Status</span>
          <select name="status" defaultValue={campaign?.status ?? "idea"}>
            {CAMPAIGN_STATUS_STYLES.map(style => (
              <option key={style.status} value={style.status}>{style.label}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Starts</span>
          <input type="date" name="startsOn" required defaultValue={campaign?.startsOn ?? defaultDay} />
        </label>
        <label className="field">
          <span>Ends <em>optional</em></span>
          <input type="date" name="endsOn" defaultValue={campaign?.endsOn ?? ""} />
        </label>
        <label className="field">
          <span>Channel</span>
          <input name="channel" maxLength={160} defaultValue={campaign?.channel ?? ""} placeholder="Instagram + Facebook" />
        </label>
        <label className="field">
          <span>Owner</span>
          <input name="owner" maxLength={160} defaultValue={campaign?.owner ?? ""} />
        </label>
        <label className="field mkt__editor-wide">
          <span>Post copy <em>what actually goes out</em></span>
          <textarea name="postCopy" rows={4} maxLength={8000} defaultValue={campaign?.postCopy ?? ""} />
        </label>
        <label className="field mkt__editor-wide">
          <span>Internal notes</span>
          <textarea name="notes" rows={2} maxLength={4000} defaultValue={campaign?.notes ?? ""} />
        </label>
      </div>

      {error && <p className="mkt__editor-error">{error}</p>}

      <div className="mkt__editor-actions">
        <button className="button button--coral" disabled={busy}>{busy ? "Saving…" : "Save campaign"}</button>
        <button type="button" className="button button--outline-dark" onClick={onClose}>Cancel</button>
        {campaign?.lastEditedBy && <small>Last edited by {campaign.lastEditedBy}</small>}
      </div>
    </form>
  );
}
